import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {useDebouncer} from "@tanstack/react-pacer";
import {SIDEBAR_WIDTH} from "crqlar-react-common/dist/utils/useDimensions";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useServerTableContext} from "../ServerTableContext";

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_PANEL_WIDTH = 380;

interface ServerTableRailSearchPanelProps {
    onClose: () => void;
}

export function ServerTableRailSearchPanel({
    onClose,
}: ServerTableRailSearchPanelProps) {
    const {t} = useTranslation();
    const {query, searchQuery, setSearchQuery} = useServerTableContext();
    const [inputValue, setInputValue] = useState(searchQuery);
    const searchDebouncer = useDebouncer(
        setSearchQuery,
        {
            wait: SEARCH_DEBOUNCE_MS,
            onUnmount: debouncer => debouncer.flush(),
        } as Parameters<typeof useDebouncer>[1],
    );
    const {cancel, maybeExecute} = searchDebouncer;

    useEffect(() => {
        cancel();
        setInputValue(searchQuery);
    }, [cancel, searchQuery]);

    const closeSearch = () => {
        cancel();
        if (inputValue !== searchQuery) setSearchQuery(inputValue);
        onClose();
    };

    const clearAndCloseSearch = () => {
        cancel();
        setInputValue("");
        if (inputValue || searchQuery) setSearchQuery("");
        onClose();
    };
    const clearAndCloseLabel = t("Clear and close search");

    return (
        <Box
            sx={{
                position: {xs: "static", md: "absolute"},
                top: {md: 0},
                right: {md: SIDEBAR_WIDTH},
                width: {
                    xs: "100%",
                    md: SEARCH_PANEL_WIDTH,
                },
                height: {xs: "100%", md: "auto"},
                minWidth: 0,
                flex: {xs: "1 1 auto", md: "0 0 auto"},
                px: {xs: 0, md: 2},
                py: {xs: 0, md: 1.25},
                boxSizing: "border-box",
                bgcolor: "background.paper",
                border: {xs: 0, md: "1px solid rgba(15, 23, 42, 0.12)"},
                borderRadius: 1,
                boxShadow: {xs: "none", md: "0 12px 28px rgba(15, 23, 42, 0.12)"},
                zIndex: {md: 40},
            }}
        >
            <TextField
                autoFocus
                fullWidth
                size="small"
                color="primary"
                sx={{
                    "& .MuiInputBase-root": {height: 36},
                    "& .MuiInputAdornment-positionEnd": {ml: 0.5},
                }}
                value={inputValue}
                placeholder={t("Search...")}
                onChange={event => {
                    const value = event.target.value;

                    setInputValue(value);
                    maybeExecute(value);
                }}
                onKeyDown={event => {
                    if (event.key === "Escape") {
                        event.stopPropagation();
                        closeSearch();
                    }
                }}
                InputProps={{
                    startAdornment: (
                    <InputAdornment position="start" sx={{color: 'primary.main'}}>
                        {query.isFetching
                            ? <CircularProgress color="inherit" size={18}/>
                                : <SearchOutlinedIcon fontSize="small"/>}
                        </InputAdornment>
                    ),
                    endAdornment: (
                    <InputAdornment position="end" sx={{color: 'primary.main'}}>
                        <Tooltip title={clearAndCloseLabel}>
                            <IconButton
                                edge="end"
                                size="small"
                                color="primary"
                                data-dashboard-control=""
                                    aria-label={clearAndCloseLabel}
                                    onClick={clearAndCloseSearch}
                                >
                                    <CloseIcon fontSize="small"/>
                            </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
}
